/**
 * Collection Fast Navigation — Shopify Section Rendering API
 *
 * Filter tabs navigate to real /collections/{handle} URLs (no ?filter=).
 * Legacy ?filter= URLs are still resolved via COLLECTION_FILTER_MAP /
 * nnh-legacy-filter-redirect.js for old bookmarks and menu links.
 */

(function () {
  'use strict';

  var GRID_ID    = 'CollectionProductGrid';
  var SECTION_ID = 'collection-page-v2';   // matches key in collection*.json templates
  var pending    = null;

  // Every handle shown in collection filter tabs — native navigation, no ?filter=
  var NNH_TAB_COLLECTION_HANDLES = [
    'pcos-supplements',
    'bundles-1',
    'hair-skin',
    'weight-support',
    'energy-mood',
    'cycle-support',
    'fertility-prenatal',
    'gut-digestion',
    'insulin-resistance',
    'adrenal-pcos',
    'post-pill-pcos',
    'inflammatory-pcos'
  ];

  // Short ?filter= slugs (legacy Shop by Support menu) → real collection handles
  var COLLECTION_FILTER_MAP = {
    'all-products': { filter: 'all', name: 'all supplements' },
    'bundles-1': { filter: 'bundle', name: 'Bundles' },
    'unwanted-hair': { filter: 'hair', name: 'unwanted hair' },
    'clear-skin-healthy-hair-collection': { filter: 'skin', name: 'Clear Skin' },
    'healthy-weight-metabolism-collection': { filter: 'weight', name: 'Weight support' },
    'mood-energy': { filter: 'mood+energy', name: 'Mood + Energy' },
    'regular-cycles-fertility-collection': { filter: 'cycles+fertility', name: 'Cycles + Fertility' },
    'cycle-support-essentials': { filter: 'cycles+fertility', name: 'Cycles + Fertility' },
    'insulin-resistance': { filter: 'insulin-resistant', name: 'Insulin Resistant PCOS' },
    'adrenal-pcos': { filter: 'adrenal', name: 'Adrenal PCOS' },
    'post-pill-pcos': { filter: 'post-pill', name: 'Post-Pill PCOS' },
    'inflammatory-pcos': { filter: 'inflammatory', name: 'Inflammatory PCOS' }
  };

  function getGrid() { return document.getElementById(GRID_ID); }

  function getHandle(href) {
    var m = (href || '').match(/\/collections\/([^\/\?#]+)/);
    return m ? m[1] : null;
  }

  function resolveCollectionHandle(value) {
    if (!value || value === 'all') return 'all-products';
    if (COLLECTION_FILTER_MAP[value]) return value;
    var handle;
    for (handle in COLLECTION_FILTER_MAP) {
      if (COLLECTION_FILTER_MAP[handle].filter === value) return handle;
    }
    return null;
  }

  function filterSlugForHandle(handle) {
    var cfg = COLLECTION_FILTER_MAP[handle];
    return cfg ? cfg.filter : handle;
  }

  // Same filter tab may use a different collection handle (e.g. legacy cycle-support-essentials)
  function tabHandleForCollection(handle) {
    if (handle === 'cycle-support-essentials') return 'regular-cycles-fertility-collection';
    return handle;
  }

  function gridHandleForCollection(handle) {
    return tabHandleForCollection(handle);
  }

  function isAllProductsPage() {
    return /\/collections\/all-products\/?$/.test(window.location.pathname);
  }

  // ─── Boot ─────────────────────────────────────────────────────────────────

  function init() {
    setupTabs();
    if (isAllProductsPage()) {
      restoreFromUrl();
    } else {
      normalizeDirectCollectionVisit();
      var currentHandle = getHandle(window.location.pathname);
      if (currentHandle && NNH_TAB_COLLECTION_HANDLES.indexOf(currentHandle) !== -1) {
        var activeTab = document.querySelector('.collection-tab[href="/collections/' + currentHandle + '"]');
        if (activeTab) scrollTabIntoView(activeTab);
      }
    }
  }

  // ─── Tab clicks ───────────────────────────────────────────────────────────

  function setupTabs() {
    document.querySelectorAll('.collection-tab').forEach(function (tab) {
      tab.addEventListener('click', function (e) {
        var handle = getHandle(tab.getAttribute('href'));
        if (!handle) return;

        // All filter tabs use real collection URLs — never intercept or add ?filter=
        if (NNH_TAB_COLLECTION_HANDLES.indexOf(handle) !== -1) {
          return;
        }

        e.preventDefault();   // never let a tab trigger a full page load
        if (tab.classList.contains('collection-tab--active')) return;

        activateTab(handle);
        loadGrid(handle, true);
      });
    });
  }

  // ─── Restore on ?filter= direct URL visit ────────────────────────────────

  function restoreFromUrl() {
    var filter = new URLSearchParams(window.location.search).get('filter');
    if (!filter) return;

    var handle = resolveCollectionHandle(filter);
    if (!handle) {
      console.warn('[NNH] Unknown collection filter slug:', filter);
      return;
    }

    activateTab(tabHandleForCollection(handle));
    loadGrid(gridHandleForCollection(handle), false);
  }

  // Landed on legacy mapped handles — keep URL as real /collections/{handle} for tab collections
  function normalizeDirectCollectionVisit() {
    if (!getGrid()) return;

    var handle = getHandle(window.location.pathname);
    if (!handle || !COLLECTION_FILTER_MAP[handle]) return;

    // Filter-tab collections stay on their own URL (no ?filter= rewrite)
    if (NNH_TAB_COLLECTION_HANDLES.indexOf(handle) !== -1) {
      activateTab(handle);
      return;
    }

    var filterSlug = filterSlugForHandle(handle);
    var tabHandle = tabHandleForCollection(handle);
    var hubUrl = handle === 'all-products'
      ? '/collections/all-products'
      : '/collections/all-products?filter=' + encodeURIComponent(filterSlug);

    history.replaceState({ handle: tabHandle }, '', hubUrl);
    activateTab(tabHandle);
    loadGrid(gridHandleForCollection(handle), false);
  }

  // ─── Fetch only the section HTML via ?section_id= (fast, ~30KB) ──────────

  function loadGrid(handle, pushHistory) {
    if (pending) pending.abort();
    var ctrl = new AbortController();
    pending  = ctrl;

    showLoading();

    // ?section_id= returns just this section's rendered HTML as plain text —
    // no JSON, no key matching, much smaller than a full page fetch.
    fetch('/collections/' + handle + '?section_id=' + SECTION_ID, {
      signal: ctrl.signal
    })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        swapGrid(html, handle, pushHistory);
        hideLoading();
        pending = null;
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        console.error('[NNH] section fetch failed for ' + handle + ':', err.message);
        hideLoading();
      });
  }

  // ─── Parse and inject grid, count and title ───────────────────────────────

  function swapGrid(sectionHtml, handle, pushHistory) {
    var doc     = new DOMParser().parseFromString(sectionHtml, 'text/html');
    var newGrid = doc.getElementById(GRID_ID);
    var curGrid = getGrid();

    if (!newGrid || !curGrid) {
      console.error('[NNH] #' + GRID_ID + ' not found in section HTML for ' + handle);
      return;
    }

    curGrid.innerHTML = newGrid.innerHTML;

    // Sync product count
    var nc = doc.querySelector('.collection-toolbar__count');
    var cc = document.querySelector('.collection-toolbar__count');
    if (nc && cc) cc.textContent = nc.textContent;

    // Sync hero title
    var nt = doc.querySelector('.collection-hero__title');
    var ct = document.querySelector('.collection-hero__title');
    if (nt && ct) ct.textContent = nt.textContent;

    // URL always anchors to /collections/all-products regardless of starting page
    if (pushHistory) {
      var filterSlug = filterSlugForHandle(handle);
      var filterUrl = handle === 'all-products'
        ? '/collections/all-products'
        : '/collections/all-products?filter=' + encodeURIComponent(filterSlug);
      history.pushState({ handle: handle }, '', filterUrl);
    }

    // Re-initialise Loox review stars on newly injected cards
    if (window.Loox && typeof window.Loox.renderAll === 'function') {
      try { window.Loox.renderAll(); } catch (ignore) {}
    }
  }

  // ─── Active tab state ─────────────────────────────────────────────────────

  function activateTab(handle) {
    document.querySelectorAll('.collection-tab').forEach(function (tab) {
      var active = getHandle(tab.getAttribute('href')) === handle;
      tab.classList.toggle('collection-tab--active', active);
      if (active) {
        tab.setAttribute('aria-current', 'page');
        scrollTabIntoView(tab);
      } else {
        tab.removeAttribute('aria-current');
      }
    });
  }

  function scrollTabIntoView(tab) {
    var c = document.getElementById('collectionTabs');
    if (!c) return;
    var cr = c.getBoundingClientRect();
    var tr = tab.getBoundingClientRect();
    c.scrollTo({
      left: Math.max(0, c.scrollLeft + (tr.left - cr.left) + tr.width / 2 - c.clientWidth / 2),
      behavior: 'smooth'
    });
  }

  // ─── Loading state ────────────────────────────────────────────────────────

  function showLoading() {
    var g = getGrid();
    if (!g) return;
    g.style.transition    = 'opacity 0.15s ease';
    g.style.opacity       = '0.4';
    g.style.pointerEvents = 'none';
  }

  function hideLoading() {
    var g = getGrid();
    if (!g) return;
    g.style.opacity       = '';
    g.style.pointerEvents = '';
    setTimeout(function () { g.style.transition = ''; }, 300);
  }

  // ─── Browser back / forward ───────────────────────────────────────────────

  window.addEventListener('popstate', function (e) {
    if (!isAllProductsPage()) return;

    var fromState = e.state && e.state.handle;
    var fromQuery = new URLSearchParams(window.location.search).get('filter');
    var handle = resolveCollectionHandle(fromState || fromQuery || 'all');
    if (!handle) return;

    activateTab(handle);
    loadGrid(handle, false);
  });

  // ─── Init ─────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
